<template>
    <div>
        <div class="flex mb-4 items-center">
            <div class="w-1/2">
                <img srcset="@/assets/images/ic-logo-eostre.jpg,
                    @/assets/images/ic-logo-eostre@2x.jpg 2x,
                    @/assets/images/ic-logo-eostre@3x.jpg 3x"
                    src="@/assets/images/ic-logo-eostre@3x.jpg"
                    alt="Eostre logo"
                >
            </div>
            <div class="normal text-right w-1/2">
                <LanguageSelector></LanguageSelector>
            </div>
        </div>

        <h1 class="caption blue-dark mb-8">{{ $t('registration.signup_title') }}</h1>

        <form>
            <input
                type="text"
                required
                class="w-full mb-4 mt-1"
                v-bind:placeholder="$t('registration.username')"
                v-model.lazy="username"
            />

            <input
                type="password"
                required
                class="w-full mb-4"
                ref="password"
                v-bind:placeholder="$t('registration.password')"
                autocomplete="new-password"
                @input="passwordChanged"
            />

            <input
                type="password"
                required
                class="w-full mb-4"
                ref="confirmPassword"
                v-bind:placeholder="$t('registration.confirm_password')"
                autocomplete="new-password"
                @input="passwordChanged"
            />

            <VuePhoneNumberInput
                v-model="phoneNumber"
                :no-example="true"
                class="mb-4"
                :translations="phoneTranslation"
                :default-country-code="defaultCountryCode"
                :format-as-you-type="false"
                @update="phoneNumberChanged"
            />
        </form>

        <div class="bg-snow referral rounded mb-4">
            <div class="tiny blue-dark">{{ $t('registration.referral_username') }}</div>
            <div class="black regular">{{ referralCode }}</div>
        </div>

        <p class="normal error" v-if="errors.length > 0">
            <ol>
                <template v-for="(error, i) in errors">
                    <li :key="`error-${i}`">{{ error }}</li>
                </template>
            </ol>
        </p>

        <button class="w-full rounded mt-8" :disabled="!completedProfile" @click="next">{{ $t('common.continue') }}</button>

        <div class="small grey-dark mt-5">{{ $t('registration.policy_statement') }}</div>
    </div>
</template>

<style lang="scss">
@import '@/assets/css/color';
@import '@/assets/css/typography';

.referral {
    padding: 11px 8px;
    height: 50px;
}

.country-selector__list button {
    color: $blue-dark;
}

input[readonly].country-selector__input, input[type=tel].input-tel__input {
    @extend .regular;
    background: $grey-lighter;
    height: 50px;
    border: 0;
    transition: none;

    &::placeholder {
        color: $purple-lighter;
    }

    &:focus {
        border: 0 !important;
        box-shadow: none !important;
        outline: 5px auto -webkit-focus-ring-color;
    }
}

.country-selector.input-country-selector.has-value {
    height: 50px;

    .country-selector__country-flag {
       top: 26px;
    }
}
</style>

<script>
import Store from '@/store'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'

const DEFAULT_COUNTRY_CODE = process.env.VUE_APP_DEFAULT_COUNTRY_CODE

export default {
    data () {
        return {
            phoneTranslation: {},
            defaultCountryCode: DEFAULT_COUNTRY_CODE,
            phoneNumber: '',
            errors: []
        }
    },
    computed: {
        username: {
            get () { return this.$store.state.profile.username },
            set (value) { this.$store.dispatch('setUsername', value) }
        },
        name: {
            get () { return this.$store.state.profile.name },
            set (value) { this.$store.dispatch('setName', value) }
        },
        referralCode () { return this.$store.state.profile.referralCode },
        completedProfile () { return this.$store.getters.completedProfile && !this.error },
        profile () { return this.$store.state.profile }
    },
    components: {
        LanguageSelector: () => import('@/components/LanguageSelector'),
        VuePhoneNumberInput: () => import('vue-phone-number-input')
    },
    created () {
        this.$store.dispatch('setPassword', '')
        this.phoneNumber = this.profile.phoneNumber
    },
    mounted () {
        this.refreshLanguage()
    },
    methods: {
        refreshLanguage () {
            this.phoneTranslation = {
                countrySelectorLabel: this.$t('registration.phone.country_code'),
                countrySelectorError: this.$t('registration.phone.country_error'),
                phoneNumberLabel: this.$t('registration.phone.phone_number'),
                example: this.$t('registration.phone.example'),
            }
        },
        phoneNumberChanged (phoneNumber) {
            if (phoneNumber.isValid) {
                this.$store.dispatch('setPhone', phoneNumber.formattedNumber)
            } else {
                this.$store.dispatch('setPhone', '')
            }
        },
        passwordChanged () {
            const password = this.$refs.password.value
            const confirmPassword = this.$refs.confirmPassword.value

            if (password === confirmPassword) {
                this.$store.dispatch('setPassword', password)
                this.errors = []
            } else {
                this.$store.dispatch('setPassword', '')
                this.errors = [this.$t('registration.password_not_match')]
            }
        },
        async next () {
            try {
                const params = Object.assign({}, this.profile);
                if (params.phoneNumber) {
                    params.phoneNumber = String(params.phoneNumber).replace('+', '')
                }
                const result = await this.$axios.post('auth/otp?isResend=false', params)

                this.$store.dispatch('setOTPTimeout', result.data.retryWaitSeconds)

                if (this.completedProfile) {
                    this.$router.push({ path: '/otp', query: { lang: this.$store.state.lang } })
                }
            } catch (e) {
                if (e.response.data.errors) {
                    this.errors = e.response.data.errors
                } else if(e.response.data.message) {
                    this.errors = [e.response.data.message]
                }
            }
        }
    },
    watch: {
        '$store.state.lang' (lang) {
            const langParts = lang.split('-')

            if (langParts.length === 2 && langParts[0].toLowerCase() === 'zh') {
                this.defaultCountryCode = langParts[1]
            } else {
                this.defaultCountryCode = DEFAULT_COUNTRY_CODE
            }

            this.refreshLanguage()
        }
    },
    beforeRouteEnter (to, from, next) {
        if (to.params.referral) {
            Store.dispatch('setReferral', to.params.referral.trim())
        }

        next()
    }
}
</script>
